import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { ArrowUpRight, Calendar } from "lucide-react";
import { useLang, useLocalePath } from "../contexts/LanguageContext";
import SEO, { organizationSchema } from "../components/SEO";

export default function Blog() {
  const { t } = useLang();
  const localePath = useLocalePath();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/blog"); setPosts(data); }
      catch {}
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <SEO metaKey="blog" pathname="/blog" schemas={[organizationSchema()]} />

      <section className="relative isolate bg-forest text-white">
        <div className="absolute inset-0 z-0">
          <img src="/images/gallery/lsp-21.jpg" alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/55 md:bg-forest/75" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40 min-h-[60vh] flex flex-col justify-end">
          <Reveal>
            <div className="editorial-divider mb-6 max-w-xs" style={{ color: "#E3A839" }}>{t("blog_page.eyebrow")}</div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.96] max-w-5xl">
              {t("blog_page.h1a")} <br /> <span className="italic text-mustard">{t("blog_page.h1b")}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          {loading ? (
            <div className="text-center text-ink/60 py-16">{t("common.loading")}</div>
          ) : posts.length === 0 ? (
            <div className="text-center text-ink/60 py-16">{t("blog_page.empty")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <Link to={localePath(`/blog/${p.slug}`)} data-testid={`blog-card-${p.slug}`} className="group block h-full">
                    <div className="h-64 overflow-hidden rounded-xl bg-kraft/40 mb-5">
                      <img src={p.cover_image || "/images/gallery/lsp-4.jpg"} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    </div>
                    <div className="flex items-center gap-4 text-[12px] tracking-[0.2em] uppercase text-clay mb-3">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(p.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {p.tags?.[0] && <span>· {p.tags[0]}</span>}
                    </div>
                    <h2 className="font-serif text-2xl lg:text-3xl text-forest leading-snug group-hover:text-terracotta transition-colors">{p.title}</h2>
                    <p className="mt-3 text-ink/70 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-forest font-medium text-[14px]">
                      {t("common.read_more")} <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
